import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../../components/AdminLayout";
import { api } from "../../lib/api";
import { Plus, Edit3, Trash2, ExternalLink, FileText, CalendarClock } from "lucide-react";
import { toast } from "sonner";

export default function AdminBlogList() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/blog");
      setPosts(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/${slug}`);
      toast.success("Deleted.");
      await load();
    } catch { toast.error("Could not delete."); }
  };

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="eyebrow mb-2">Blog</div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Posts</h1>
          <p className="text-inkSoft mt-1">Publish and manage articles. New posts appear on the site instantly — no redeploy needed.</p>
        </div>
        <Link to="/admin/blog/new" className="btn-primary text-sm" data-testid="admin-blog-new">
          <Plus size={16} /> New post
        </Link>
      </div>

      <div className="mt-6 rounded-2xl border border-line bg-white overflow-hidden">
        {loading ? (
          <div className="px-6 py-10 text-sm text-inkSoft">Loading…</div>
        ) : posts.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <FileText size={24} className="mx-auto text-inkSoft" />
            <div className="text-sm text-inkSoft mt-2">No posts yet.</div>
            <Link to="/admin/blog/new" className="btn-primary text-sm mt-4 inline-flex">Create your first post</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-inkSoft border-b border-line bg-surfaceAlt/40">
                <th className="px-6 py-4 font-medium">Title</th>
                <th className="px-6 py-4 font-medium">Category</th>
                <th className="px-6 py-4 font-medium">Status</th>
                <th className="px-6 py-4 font-medium">Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr key={p.slug} className="border-b border-line last:border-0 hover:bg-surfaceAlt/30" data-testid={`admin-blog-row-${p.slug}`}>
                  <td className="px-6 py-4">
                    <div className="font-medium">{p.title}</div>
                    <div className="text-xs text-inkSoft font-mono">/blog/{p.slug}</div>
                  </td>
                  <td className="px-6 py-4"><span className="tag">{p.category || "SEO"}</span></td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                        p.status === "published"
                          ? "bg-emerald-100 text-emerald-700"
                          : p.status === "scheduled"
                          ? "bg-sky-100 text-sky-800"
                          : "bg-amber-100 text-amber-800"
                      }`}
                      data-testid={`admin-blog-status-${p.slug}`}
                    >
                      {p.status}
                    </span>
                    {p.status === "scheduled" && p.scheduled_for && (
                      <div className="text-[11px] text-inkSoft mt-1 inline-flex items-center gap-1">
                        <CalendarClock size={11} />
                        {new Date(p.scheduled_for).toLocaleString(undefined, {
                          month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-inkSoft text-xs">{p.updated_at ? new Date(p.updated_at).toLocaleDateString() : "—"}</td>
                  <td className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-3">
                      {p.status === "published" && (
                        <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="text-inkSoft hover:text-ink" title="View"><ExternalLink size={14} /></a>
                      )}
                      <Link to={`/admin/blog/edit/${p.slug}`} className="text-terra hover:underline inline-flex items-center gap-1 text-sm font-medium"><Edit3 size={14} /> Edit</Link>
                      <button onClick={() => remove(p.slug)} className="text-red-600 hover:bg-red-50 h-8 w-8 grid place-items-center rounded-lg"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}
